import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { Loader2 } from 'lucide-react';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from 'recharts';

type CostsGraphProps = {
  dateFrom: string;
  dateTo: string;
  selectedCountries: string[];
  selectedCampaigns: string[];
  selectedPlatforms: string[];
  viewMode: 'daily' | 'monthly' | 'weekly';
  showWithTax: boolean;
  vatRates: { [country: string]: number };
};

type Point = {
  date: string;   // already formatted for axis
  costs: number;
  revenue: number;
  roi: number;
  predicted_roi: number;
  early_predicted_roi: number;
};

type MergedPoint = Point & {
  prev_costs?: number | null;
  prev_revenue?: number | null;
  prev_roi?: number | null;
  prev_predicted_roi?: number | null;
  prev_early_predicted_roi?: number | null;
};

// Контрастная палитра (colorblind-friendly)
const COLORS = {
  costs: '#E15759',              // красный
  revenue: '#59A14F',            // зелёный
  roi: '#4E79A7',                // синий
  predicted_roi: '#B07AA1',      // фиолетовый
  early_predicted_roi: '#F28E2B' // оранжевый
} as const;

const NAME_MAP: Record<keyof Point, string> = {
  date: 'Date',
  costs: 'Costs',
  revenue: 'Revenue',
  roi: 'ROI (%)',
  predicted_roi: 'Predicted ROI (%)',
  early_predicted_roi: 'Early Predicted ROI (%)',
};

// Небольшой помощник для rgba из hex
const withOpacity = (hex: string, opacity: number) => {
  const h = hex.replace('#','');
  const bigint = parseInt(h, 16);
  const r = (bigint >> 16) & 255;
  const g = (bigint >> 8) & 255;
  const b = bigint & 255;
  return `rgba(${r}, ${g}, ${b}, ${opacity})`;
};

const CostsGraphComponent: React.FC<CostsGraphProps> = ({
  dateFrom,
  dateTo,
  selectedCountries,
  selectedCampaigns,
  selectedPlatforms,
  viewMode,
  showWithTax,
  vatRates,
}) => {
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [chartData, setChartData] = useState<Point[]>([]);

  // previous period
  const [showPrev, setShowPrev] = useState(false);
  const [prevLoading, setPrevLoading] = useState(false);
  const [prevError, setPrevError] = useState<string | null>(null);
  const [prevChartData, setPrevChartData] = useState<Point[]>([]);

  // visibility toggles
  const [visible, setVisible] = useState<Record<keyof Omit<Point, 'date'>, boolean>>({
    costs: true,
    revenue: true,
    roi: true,
    predicted_roi: true,
    early_predicted_roi: true,
  });

  // ===== Helpers =====
  const toYMD = (d: Date) => d.toISOString().slice(0, 10);
  const diffDaysInclusive = (a: string, b: string) =>
    Math.round((+new Date(b) - +new Date(a)) / 86400000) + 1;

  const getPrevRange = (from: string, to: string) => {
    const days = diffDaysInclusive(from, to);
    const start = new Date(from);
    const prevEnd = new Date(+start - 86400000);
    const prevStart = new Date(+prevEnd - (days - 1) * 86400000);
    return { prevFrom: toYMD(prevStart), prevTo: toYMD(prevEnd) };
  };

  const mergeSeries = (curr: Point[], prev: Point[]): MergedPoint[] =>
    curr.map((p, idx) => ({
      ...p,
      prev_costs: prev[idx]?.costs ?? null,
      prev_revenue: prev[idx]?.revenue ?? null,
      prev_roi: prev[idx]?.roi ?? null,
      prev_predicted_roi: prev[idx]?.predicted_roi ?? null,
      prev_early_predicted_roi: prev[idx]?.early_predicted_roi ?? null,
    }));

  // ===== Numeric & VAT helpers =====
  const getNumericValue = (value: any): number => {
    if (value === null || value === undefined || value === '') return 0;
    const num = parseFloat(String(value));
    return isNaN(num) ? 0 : num;
  };

  const getVatRate = (country: string): number =>
    vatRates[country] !== undefined ? vatRates[country] : 0.0;

  const getAdjustedRevenue = (value: number, country: string): number => {
    const numericValue = getNumericValue(value);
    if (!showWithTax) return numericValue;
    const vatRate = getVatRate(country);
    return numericValue * (1 - vatRate);
  };

  const getHistoricCoeff = (item: any): number =>
    getNumericValue(item.historic_coeff) || 1;

  // ===== Revenue blocks (per item) =====
  const calculateTotalRevenue = (item: any, country: string): number => {
    let total =
      getAdjustedRevenue(getNumericValue(item.revenue_activation_original ?? item.revenue_activation), country) +
      getAdjustedRevenue(getNumericValue(item.revenue_activation_purchase), country);

    for (let i = 1; i <= 12; i++) {
      total += getAdjustedRevenue(getNumericValue(item[`revenue_p${i}`]), country);
    }
    return total;
  };

  // Predicted revenue
  const calculateTotalPredictedRevenue = (item: any, country: string): number => {
    let total =
      getAdjustedRevenue(getNumericValue(item.revenue_activation_original ?? item.revenue_activation), country) +
      getAdjustedRevenue(getNumericValue(item.revenue_activation_purchase), country);

    let lastCalculatedValue = 0;
    for (let i = 1; i <= 12; i++) {
      const actual = getNumericValue(item[`revenue_p${i}`]);
      let predicted = 0;
      if (actual > 0) {
        predicted = actual;
      } else {
        const apiPred = getNumericValue(item[`predicted_revenue_p${i}`]);
        if (apiPred > 0) {
          predicted = apiPred;
        } else if (i > 1) {
          const churn = getNumericValue(item[`predicted_churn_p${i}`]); // %
          predicted = lastCalculatedValue * (churn / 100.0);
        } else {
          predicted = 0;
        }
      }
      total += getAdjustedRevenue(predicted, country);
      lastCalculatedValue = predicted;
    }
    return total;
  };

  // Early predicted
  const calculateEarlyPredictedRevenue = (item: any, country: string): number => {
    const historicCoeff = getHistoricCoeff(item);
    const revenueBeforeP1 = getAdjustedRevenue(getNumericValue(item.revenue_before_p1_purchase), country);
    const revenueActivationOriginal = getAdjustedRevenue(getNumericValue(item.revenue_activation_original ?? item.revenue_activation), country);

    const part1 = revenueActivationOriginal + (revenueBeforeP1 * historicCoeff);

    let sumEarly = 0;
    let currentValue = getNumericValue(item.revenue_p1);
    sumEarly += getAdjustedRevenue(currentValue, country); // P1

    for (let i = 2; i <= 12; i++) {
      const churn = getNumericValue(item[`predicted_churn_p${i}`]); // %
      if (currentValue <= 0 || churn <= 0) currentValue = 0;
      else currentValue = currentValue * (churn / 100.0);
      sumEarly += getAdjustedRevenue(currentValue, country);
    }
    return part1 + sumEarly;
  };

  // ===== Common formatter for API -> chart points =====
  const formatApiToPoints = (apiData: any): Point[] => {
    const data = apiData?.data || {};
    const formatted: Point[] = [];

    const sortedDates = Object.keys(data).sort();
    sortedDates.forEach(date => {
      // label
      let label: string;
      if (viewMode === 'monthly') {
        label = new Date(date + '-01').toLocaleDateString('default', { month: 'short', year: 'numeric' });
      } else if (viewMode === 'weekly') {
        if (date.includes(' - ')) {
          const [start, end] = date.split(' - ');
          label = `${new Date(start).toLocaleDateString('default', { day: 'numeric', month: 'short' })} - ${new Date(end).toLocaleDateString('default', { day: 'numeric', month: 'short' })}`;
        } else {
          label = new Date(date).toLocaleDateString('default', { day: 'numeric', month: 'short', year: 'numeric' });
        }
      } else {
        label = new Date(date).toLocaleDateString();
      }

      let totalCosts = 0;
      let totalRevenue = 0;
      let totalPredicted = 0;
      let totalEarlyPredicted = 0;

      Object.entries<any>(data[date]).forEach(([country, countryItems]) => {
        if (selectedCountries.length === 0 || selectedCountries.includes(country)) {
          countryItems.forEach((item: any) => {
            const campaignMatch = selectedCampaigns.length === 0 || selectedCampaigns.includes(item.campaign);
            const platformMatch = selectedPlatforms.length === 0 || selectedPlatforms.includes(item.platform || '---');
            if (campaignMatch && platformMatch) {
              totalCosts += getNumericValue(item.costs);
              totalRevenue += calculateTotalRevenue(item, country);
              totalPredicted += calculateTotalPredictedRevenue(item, country);
              totalEarlyPredicted += calculateEarlyPredictedRevenue(item, country);
            }
          });
        }
      });

      const roi = totalCosts > 0 ? ((totalRevenue - totalCosts) / totalCosts) * 100 : 0;
      const predicted_roi = totalCosts > 0 ? ((totalPredicted - totalCosts) / totalCosts) * 100 : 0;
      const early_predicted_roi = totalCosts > 0 ? ((totalEarlyPredicted - totalCosts) / totalCosts) * 100 : 0;

      formatted.push({
        date: label,
        costs: totalCosts,
        revenue: totalRevenue,
        roi,
        predicted_roi,
        early_predicted_roi
      });
    });

    return formatted;
  };

  // ===== Fetch current period =====
  useEffect(() => {
    const fetchGraphData = async () => {
      setLoading(true);
      setError(null);
      // reset previous period on filters change
      setShowPrev(false);
      setPrevChartData([]);
      setPrevError(null);

      try {
        const endpoint = 'https://dashboard-nk.golive.mobi/api/wow/api/orders';
        const params: Record<string, any> = {};

        if (viewMode === 'daily') params.keyevent = 'days';
        else if (viewMode === 'weekly') params.keyevent = 'weeks';
        else if (viewMode === 'monthly') params.keyevent = 'month';

        if (selectedCountries.length > 0) params.country_filter = selectedCountries.join(',');
        if (selectedCampaigns.length > 0) params.campaign = selectedCampaigns.join(',');

        if (dateFrom) params.date_from = dateFrom;
        if (dateTo) params.date_to = dateTo;

        const res = await axios.get(endpoint, { params });
        setChartData(formatApiToPoints(res.data));
      } catch (e) {
        console.error(e);
        setError('Failed to fetch graph data');
      } finally {
        setLoading(false);
      }
    };

    fetchGraphData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dateFrom, dateTo, selectedCountries, selectedCampaigns, selectedPlatforms, viewMode, showWithTax, vatRates]);

  // ===== Fetch previous period (on demand) =====
  const fetchPrevPeriod = async () => {
    setPrevLoading(true);
    setPrevError(null);
    try {
      const { prevFrom, prevTo } = getPrevRange(dateFrom, dateTo);
      const endpoint = 'https://dashboard-nk.golive.mobi/api/wow/api/orders';
      const params: Record<string, any> = {};

      if (viewMode === 'daily') params.keyevent = 'days';
      else if (viewMode === 'weekly') params.keyevent = 'weeks';
      else if (viewMode === 'monthly') params.keyevent = 'month';

      if (selectedCountries.length > 0) params.country_filter = selectedCountries.join(',');
      if (selectedCampaigns.length > 0) params.campaign = selectedCampaigns.join(',');

      params.date_from = prevFrom;
      params.date_to = prevTo;

      const res = await axios.get(endpoint, { params });
      setPrevChartData(formatApiToPoints(res.data));
    } catch (e) {
      console.error(e);
      setPrevError('Failed to fetch previous period');
      setPrevChartData([]);
    } finally {
      setPrevLoading(false);
    }
  };

  const onTogglePrev = async () => {
    if (!showPrev && prevChartData.length === 0) {
      await fetchPrevPeriod();
    }
    setShowPrev(v => !v);
  };

  const mergedData: MergedPoint[] = useMemo(
    () => mergeSeries(chartData, showPrev ? prevChartData : []),
    [chartData, prevChartData, showPrev]
  );

  // ===== UI helpers =====
  const getXAxisLabel = () => {
    switch (viewMode) {
      case 'daily': return 'Date';
      case 'weekly': return 'Week Range';
      case 'monthly': return 'Month';
      default: return 'Period';
    }
  };

  const formatCurrencyShort = (val: number) => {
    const sign = val < 0 ? '-' : '';
    const abs = Math.abs(val);
    if (abs >= 1_000_000) return `${sign}$${(abs / 1_000_000).toFixed(1)}m`;
    if (abs >= 1_000) return `${sign}$${(abs / 1_000).toFixed(1)}k`;
    return `${sign}$${abs.toFixed(0)}`;
  };

  const tooltipFormatter = (value: any, name: any, props: any) => {
    const key: string = props.dataKey || '';
    const isPct = key.includes('roi');
    if (typeof value !== 'number') return [value, name];
    return [isPct ? `${value.toFixed(2)}%` : `$${value.toFixed(2)}`, name];
  };

  const TogglePill: React.FC<{
    active: boolean;
    label: string;
    color?: string;
    onClick: () => void;
  }> = ({ active, label, color, onClick }) => (
    <button
      onClick={onClick}
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 8,
        border: 'none',
        borderRadius: 10,
        padding: '6px 10px',
        cursor: 'pointer',
        backgroundColor: active ? '#111827' : '#E5E7EB',
        color: active ? '#fff' : '#111827',
        boxShadow: '0 1px 2px rgba(0,0,0,0.1)',
      }}
      title={active ? `Hide ${label}` : `Show ${label}`}
    >
      {color && (
        <span
          style={{
            width: 10,
            height: 10,
            borderRadius: '50%',
            backgroundColor: color,
            display: 'inline-block',
          }}
        />
      )}
      {label}
    </button>
  );

  if (loading) {
    return (
      <div style={{
        backgroundColor: 'white',
        borderRadius: '12px',
        margin: '20px 30px',
        padding: '20px',
        boxShadow: '0 4px 10px rgba(0,0,0,0.06)',
        height: '420px',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center'
      }}>
        <Loader2 size={48} style={{ animation: 'spin 1s linear infinite', color: COLORS.roi }} />
      </div>
    );
  }

  if (error) {
    return (
      <div style={{
        backgroundColor: 'white',
        borderRadius: '12px',
        margin: '20px 30px',
        padding: '20px',
        boxShadow: '0 4px 10px rgba(0,0,0,0.06)',
        color: 'red'
      }}>
        {error}
      </div>
    );
  }

  return (
    <div style={{
      backgroundColor: 'white',
      borderRadius: '12px',
      margin: '20px 30px',
      padding: '20px',
      boxShadow: '0 4px 10px rgba(0,0,0,0.06)'
    }}>
      <div style={{ marginBottom: 8 }}>
        <h2 style={{ margin: 0, fontSize: 18, fontWeight: 700, color: '#111827' }}>
          Performance Metrics Over Time
        </h2>
        <div style={{ color: '#6B7280', fontSize: 12 }}>Overall performance metrics</div>
      </div>

      {/* Legend toggles */}
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: 12 }}>
        <TogglePill
          active={visible.costs}
          label="Costs"
          color={COLORS.costs}
          onClick={() => setVisible(v => ({ ...v, costs: !v.costs }))}
        />
        <TogglePill
          active={visible.revenue}
          label="Revenue"
          color={COLORS.revenue}
          onClick={() => setVisible(v => ({ ...v, revenue: !v.revenue }))}
        />
        <TogglePill
          active={visible.roi}
          label="ROI (%)"
          color={COLORS.roi}
          onClick={() => setVisible(v => ({ ...v, roi: !v.roi }))}
        />
        <TogglePill
          active={visible.predicted_roi}
          label="Predicted ROI"
          color={COLORS.predicted_roi}
          onClick={() => setVisible(v => ({ ...v, predicted_roi: !v.predicted_roi }))}
        />
        <TogglePill
          active={visible.early_predicted_roi}
          label="Early Predicted ROI"
          color={COLORS.early_predicted_roi}
          onClick={() => setVisible(v => ({ ...v, early_predicted_roi: !v.early_predicted_roi }))}
        />
        <TogglePill
          active={showPrev}
          label={prevLoading ? 'Loading…' : 'Previous Period'}
          onClick={onTogglePrev}
        />
      </div>
      {prevError && <div style={{ color: '#dc3545', marginBottom: 8 }}>{prevError}</div>}

      <div style={{ width: '100%', height: 420 }}>
        <ResponsiveContainer>
          <LineChart data={mergedData} margin={{ top: 20, right: 30, left: 20, bottom: 60 }}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis
              dataKey="date"
              label={{ value: getXAxisLabel(), position: 'insideBottom', offset: -40 }}
              angle={-45}
              textAnchor="end"
              height={60}
            />
            {/* Left Y axis = money, Right Y axis = percent */}
            <YAxis
              yAxisId="left"
              label={{ value: 'Amount', angle: -90, position: 'insideLeft', style: { textAnchor: 'middle' } }}
              tickFormatter={formatCurrencyShort}
            />
            <YAxis
              yAxisId="right"
              orientation="right"
              label={{ value: 'ROI (%)', angle: -90, position: 'insideRight', style: { textAnchor: 'middle' } }}
              tickFormatter={(v) => `${v}%`}
            />
            <Tooltip formatter={tooltipFormatter} />

            {/* Current period lines (толще и чёткие цвета) */}
            {visible.costs && (
              <Line
                yAxisId="left"
                type="monotone"
                dataKey="costs"
                name={NAME_MAP.costs}
                stroke={COLORS.costs}
                strokeWidth={2.5}
                dot={false}
                activeDot={{ r: 6 }}
              />
            )}
            {visible.revenue && (
              <Line
                yAxisId="left"
                type="monotone"
                dataKey="revenue"
                name={NAME_MAP.revenue}
                stroke={COLORS.revenue}
                strokeWidth={2.5}
                dot={false}
                activeDot={{ r: 6 }}
              />
            )}
            {visible.roi && (
              <Line
                yAxisId="right"
                type="monotone"
                dataKey="roi"
                name={NAME_MAP.roi}
                stroke={COLORS.roi}
                strokeWidth={2.5}
                dot={false}
                activeDot={{ r: 6 }}
              />
            )}
            {visible.predicted_roi && (
              <Line
                yAxisId="right"
                type="monotone"
                dataKey="predicted_roi"
                name={NAME_MAP.predicted_roi}
                stroke={COLORS.predicted_roi}
                strokeWidth={2.5}
                dot={false}
                activeDot={{ r: 6 }}
              />
            )}
            {visible.early_predicted_roi && (
              <Line
                yAxisId="right"
                type="monotone"
                dataKey="early_predicted_roi"
                name={NAME_MAP.early_predicted_roi}
                stroke={COLORS.early_predicted_roi}
                strokeWidth={2.5}
                dot={false}
                activeDot={{ r: 6 }}
              />
            )}

            {/* Previous period = те же цвета, но полупрозрачные и пунктир */}
            {showPrev && visible.costs && (
              <Line
                yAxisId="left"
                type="monotone"
                dataKey="prev_costs"
                name="Costs (prev)"
                stroke={withOpacity(COLORS.costs, 0.6)}
                strokeOpacity={0.6}
                strokeWidth={2}
                strokeDasharray="6 6"
                dot={false}
              />
            )}
            {showPrev && visible.revenue && (
              <Line
                yAxisId="left"
                type="monotone"
                dataKey="prev_revenue"
                name="Revenue (prev)"
                stroke={withOpacity(COLORS.revenue, 0.6)}
                strokeOpacity={0.6}
                strokeWidth={2}
                strokeDasharray="6 6"
                dot={false}
              />
            )}
            {showPrev && visible.roi && (
              <Line
                yAxisId="right"
                type="monotone"
                dataKey="prev_roi"
                name="ROI (prev)"
                stroke={withOpacity(COLORS.roi, 0.6)}
                strokeOpacity={0.6}
                strokeWidth={2}
                strokeDasharray="6 6"
                dot={false}
              />
            )}
            {showPrev && visible.predicted_roi && (
              <Line
                yAxisId="right"
                type="monotone"
                dataKey="prev_predicted_roi"
                name="Predicted ROI (prev)"
                stroke={withOpacity(COLORS.predicted_roi, 0.6)}
                strokeOpacity={0.6}
                strokeWidth={2}
                strokeDasharray="6 6"
                dot={false}
              />
            )}
            {showPrev && visible.early_predicted_roi && (
              <Line
                yAxisId="right"
                type="monotone"
                dataKey="prev_early_predicted_roi"
                name="Early Predicted ROI (prev)"
                stroke={withOpacity(COLORS.early_predicted_roi, 0.6)}
                strokeOpacity={0.6}
                strokeWidth={2}
                strokeDasharray="6 6"
                dot={false}
              />
            )}
          </LineChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
};

export default CostsGraphComponent;